import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, Mail, ArrowLeft, ArrowRight, Clock, StopCircle, Play, Pause, CheckCircle, Timer, XCircle, Loader, FileText, AlertTriangle, Ban, MessageSquareOff, Phone, UserX, UserMinus, AlertOctagon } from 'lucide-react';
import { collection, query, where, getDoc, getDocs, orderBy, deleteDoc, doc, updateDoc, addDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions as firebaseFunctions } from '../firebase';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import InlineReplyComposer from './InlineReplyComposer';
import InlineNewPropertyCard from './InlineNewPropertyCard';
import SignaturePreview from './SignaturePreview';
import ConfirmDialog from './ConfirmDialog';
import {
  getTourActionLabel,
  isCampaignLevelActionNotification,
  isOptOutNotice,
  isRenderableDashboardActionNotification,
  isSendableActionNotification,
  selectPreferredCurrentActionNotification
} from '../utils/actionNotifications';
import { getActionCardDetails, getNeedsUserInputLabel, getActionCardRowLabel, getActionCardPropertyLabel } from '../utils/actionCardDetails';
import { getMessageDisplayParts } from '../utils/messageDisplay';
import { isPendingOutboxItem } from '../utils/outboxCounts';
import { getThreadDisplaySubject, getThreadPropertyKey, isAmbiguousStreetAnchor, threadSubjectMatchesRowAnchor } from '../utils/threadGrouping';
import { buildActionAuditPayload } from '../utils/actionAudit';
import { buildNotificationCounterUpdate } from '../utils/notificationCounters';
import { buildActiveSignatureFromProfile, isProfessionalSignatureHtml } from '../utils/signatureBuilder';
import { isNextVersionSchedulingRecord } from '../utils/baseV1Isolation';
import '../styles/ConversationsPanel.css';

// Mark Handled is an advisor decision, not a message. The audit row records it
// with a terminal status firestore.rules accepts on an owner-written create
// (queued / dismissed / stopped / completed - "handled" is PERMISSION_DENIED,
// executed against the emulator), so actionType and handledAt carry what the
// status cannot.
export const MARK_HANDLED_ACTION_TYPE = 'mark_handled';
export const MARK_HANDLED_AUDIT_STATUS = 'completed';
export const MARK_HANDLED_AUDIT_SOURCE = 'dashboard_mark_handled';

// Shown on the card when a send parked behind it cannot be cancelled. The item
// stays exactly as it was, because the alternative - a card gone and a send
// still queued - is the one outcome the advisor cannot see.
export const PARKED_SEND_UNCANCELLABLE_MESSAGE =
  'This item has a pending send that could not be cancelled. Reply to it, or use Stop.';
export const PARKED_PDF_DRAFT_UNCANCELLABLE_MESSAGE =
  'This item has a pending PDF reply that could not be cancelled. Reply to it, or use Stop.';

const PDF_MANUAL_REVIEW_REASONS = new Set([
  'needs_user_input:attachment_needs_manual_review',
  'needs_user_input:attachment_not_published'
]);

function timestampIdentity(value) {
  if (value === undefined || value === null) return null;
  if (typeof value.seconds === 'number') {
    return `${value.seconds}:${Number(value.nanoseconds || 0)}`;
  }
  if (typeof value.toMillis === 'function') return `ms:${value.toMillis()}`;
  if (typeof value.toDate === 'function') return `ms:${value.toDate().getTime()}`;
  return value;
}

function threadResolutionGeneration(data = {}) {
  return JSON.stringify([
    data.clientId ?? null,
    data.status ?? null,
    data.statusReason ?? null,
    timestampIdentity(data.statusUpdatedAt),
    timestampIdentity(data.updatedAt),
    data.followUpStatus ?? null,
    data.pdfManualReviewRequired ?? null,
    data.pdfManualReviewWarningKey ?? null,
    data.pdfManualReviewCleanupRequired ?? null,
    data.followUpConfig?.processingBy ?? null,
    timestampIdentity(data.followUpConfig?.processingAt),
    data.advisorReviewSourceMessageId ?? null,
    data.advisorReviewSourceInternetMessageId ?? null,
    data.advisorReviewSourceStatusReason ?? null,
    data.pendingTerminalReason ?? null,
    timestampIdentity(data.nonViableAt),
    timestampIdentity(data.optedOutAt)
  ]);
}

const TERMINAL_THREAD_STATUSES = new Set([
  'archived', 'cancelled', 'canceled', 'closed', 'completed', 'dismissed', 'stopped'
]);

function threadHasTerminalDisposition(data = {}) {
  return (
    TERMINAL_THREAD_STATUSES.has(String(data.status || '').trim().toLowerCase()) ||
    String(data.followUpStatus || '').trim().toLowerCase() === 'stopped' ||
    Boolean(String(data.pendingTerminalReason || '').trim()) ||
    Boolean(data.nonViableAt) ||
    Boolean(data.optedOutAt)
  );
}

// R16 D4: an advisor could only reply from a card's composer, so a
// conversation with no open card offered no way to write to the broker at all.
// The Reply control below opens the same composer with the same outbox
// document shape and the same recipient audit; the only thing it synthesises
// is the card, and that card is never written to Firestore.
//
// The server only accepts a reply on an OPEN thread
// (_validate_outbox_thread_reply_target: active or paused), so a control
// offered on a completed or stopped conversation would queue a reply that is
// dead-lettered and never sent. A conversation row here is a GROUP of threads
// for one property, so every thread in the group has to be open, and the card
// binds to the thread the message being answered actually belongs to — never
// to index 0 of the group.
const OPEN_REPLY_THREAD_STATUSES = new Set(['active', 'paused']);
const COMPLETE_REPLY_THREAD_STATUSES = new Set([
  'completed', 'complete', 'closed', 'done', 'archived', 'cancelled', 'canceled', 'dismissed'
]);

function normalizedReplyStatus(value) {
  return String(value || '').trim().toLowerCase();
}

export function manualReplySourceThreads(thread = {}) {
  const sources = Array.isArray(thread.sourceThreads) ? thread.sourceThreads : [];
  if (sources.length) return sources;
  // An ungrouped conversation stands in for its own single thread. Its id is
  // only usable when the group holds exactly one thread; otherwise there is no
  // unambiguous thread to reply on and the control stays closed.
  const threadIds = Array.isArray(thread.threadIds) ? thread.threadIds : [];
  return [{
    id: threadIds.length === 1 ? threadIds[0] : (threadIds.length ? null : thread.id || null),
    status: thread.status,
    optedOutAt: thread.optedOutAt || null,
    contactName: thread.contactName ?? null,
    rowNumber: thread.rowNumber ?? null,
    email: thread.email,
    messages: Array.isArray(thread.messages) ? thread.messages : []
  }];
}

// The newest inbound message the broker sent, with the thread it belongs to.
export function findManualReplyTarget(thread = {}) {
  let best = null;
  for (const source of manualReplySourceThreads(thread)) {
    if (!source?.id) continue;
    const messages = Array.isArray(source.messages) ? source.messages : [];
    // Inside one thread the last inbound message listed is its newest, whether
    // or not it carries a timestamp.
    let candidate = null;
    for (const message of messages) {
      if ((message?.direction || '') !== 'inbound') continue;
      if (!providerIdentity(message)) continue;
      candidate = message;
    }
    if (!candidate) continue;
    const time = Number(messageTimestampMillis(candidate)) || 0;
    if (!best || time >= best.time) best = { message: candidate, sourceThread: source, time };
  }
  if (!best) return null;
  return { message: best.message, sourceThread: best.sourceThread };
}

export function findManualReplySourceMessage(thread = {}) {
  return findManualReplyTarget(thread)?.message || null;
}

export function manualReplyAvailability(thread = {}, { pendingReplyCount = 0 } = {}) {
  const sources = manualReplySourceThreads(thread);
  const statuses = [
    normalizedReplyStatus(thread.status),
    ...sources.map(source => normalizedReplyStatus(source.status))
  ];

  if (thread.optedOutAt || sources.some(source => source.optedOutAt) || statuses.includes('opted_out')) {
    return { enabled: false, reason: 'This contact opted out, so no reply can be sent.' };
  }
  if (statuses.includes('stopped')) {
    return { enabled: false, reason: 'This conversation is stopped, so no reply can be sent.' };
  }
  if (statuses.some(status => COMPLETE_REPLY_THREAD_STATUSES.has(status))) {
    return { enabled: false, reason: 'This conversation is complete, so no reply can be sent.' };
  }
  if (statuses.some(status => !OPEN_REPLY_THREAD_STATUSES.has(status))) {
    return { enabled: false, reason: 'This conversation is not open for a reply.' };
  }
  if (Number(pendingReplyCount) > 0) {
    return { enabled: false, reason: 'A reply is already queued for this conversation.' };
  }
  if (!findManualReplyTarget(thread)) {
    return { enabled: false, reason: 'There is no broker message to reply to yet.' };
  }
  return { enabled: true, reason: '' };
}

export function buildManualReplyNotification(thread = {}, client = {}) {
  const target = findManualReplyTarget(thread);
  if (!target) return null;
  const { message: sourceMessage, sourceThread } = target;
  const replyToMessageId = sourceMessage.graphMessageId
    || sourceMessage.msGraphMessageId
    || sourceMessage.outlookMessageId
    || sourceMessage.messageId
    || sourceMessage.id
    || null;
  const sourceEmail = Array.isArray(sourceThread.email) ? sourceThread.email[0] : sourceThread.email;
  return {
    // No id: nothing in Firestore is being answered, so no notification may be
    // resolved or deleted when this reply sends.
    kind: 'action_needed',
    clientId: client?.id || null,
    // The thread that holds the message being answered, and its own contact.
    threadId: sourceThread.id,
    rowNumber: sourceThread.rowNumber ?? null,
    rowAnchor: thread.subject || null,
    email: sourceEmail || null,
    meta: {
      reason: 'needs_user_input:advisor_reply',
      details: 'Write a reply to this conversation.',
      contactName: sourceThread.contactName ?? null,
      replyToMessageId,
      sourceMessageId: sourceMessage.messageId || sourceMessage.id || replyToMessageId,
      sourceGraphMessageId: sourceMessage.graphMessageId || replyToMessageId,
      sourceInternetMessageId: sourceMessage.internetMessageId || null
    }
  };
}

function replacementReviewWarningKey(notificationId, notificationData = {}) {
  const explicit = notificationData.meta?.pdfManualReviewWarningKey;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  const sourceNotificationId = notificationData.meta?.sourceNotificationId;
  if (
    typeof sourceNotificationId !== 'string' ||
    sourceNotificationId.length === 0 ||
    sourceNotificationId !== sourceNotificationId.trim() ||
    sourceNotificationId.length > 512 ||
    sourceNotificationId.includes('/') ||
    `${sourceNotificationId}__attachment_review` !== notificationId
  ) {
    return null;
  }
  return notificationId;
}

const GRAPH_SOURCE_ID_FIELDS = [
  'msgId',
  'replyToMessageId',
  'sourceMessageId',
  'sourceGraphMessageId'
];
const INTERNET_SOURCE_ID_FIELDS = ['sourceInternetMessageId'];

function exactSourceIdentitySet(data = {}, fields = []) {
  const identities = new Set();
  for (const container of [data, data.meta]) {
    if (!container || typeof container !== 'object') continue;
    for (const field of fields) {
      const value = container[field];
      if (typeof value !== 'string') continue;
      const exact = value.trim();
      if (exact) identities.add(exact);
    }
  }
  return identities;
}

function identitySetsIntersect(left, right) {
  if (left.size === 0 || right.size === 0) return false;
  for (const value of left) {
    if (right.has(value)) return true;
  }
  return false;
}

function sourceTurnIdentitiesIntersect(card = {}, pending = {}) {
  return (
    identitySetsIntersect(
      exactSourceIdentitySet(card, GRAPH_SOURCE_ID_FIELDS),
      exactSourceIdentitySet(pending, GRAPH_SOURCE_ID_FIELDS)
    ) ||
    identitySetsIntersect(
      exactSourceIdentitySet(card, INTERNET_SOURCE_ID_FIELDS),
      exactSourceIdentitySet(pending, INTERNET_SOURCE_ID_FIELDS)
    )
  );
}

export function isActionableNotification(notif) {
  return isSendableActionNotification(notif);
}

export function notificationMatchesThread(notif, thread, threads = [thread]) {
  if (!notif || !thread) return false;

  const threadId = notif.threadId || notif.meta?.threadId;
  const hasId = candidate => candidate.id === threadId || candidate.threadIds?.includes(threadId);
  const rowAnchor = notif.rowAnchor || notif.meta?.rowAnchor || notif.meta?.propertyAddress ||
    notif.meta?.address || notif.meta?.property?.address;
  const isNewPropertyApproval = (notif.meta?.reason || notif.reason) === 'new_property_pending_approval';
  // Compatibility is checked against this thread alone: a suite-less street
  // is ambiguous across suites, but does not contradict an explicit ID or row.
  if (!(isNewPropertyApproval && threadId && hasId(thread)) && rowAnchor && thread.subject &&
      !threadSubjectMatchesRowAnchor(thread.subject, rowAnchor)) return false;

  if (threadId && threads.some(hasId)) {
    return Boolean(hasId(thread));
  }

  // Sheet row numbers may be reused after a NON-VIABLE move. Only the stable
  // thread ID can disambiguate a suite-less anchor at a shared street.
  const subjects = threads.flatMap(candidate => candidate.sourceThreads?.length
    ? candidate.sourceThreads.map(() => candidate.subject) : [candidate.subject]);
  if (isAmbiguousStreetAnchor(rowAnchor, subjects)) return false;

  const rowNumber = notif.rowNumber ?? notif.meta?.rowNumber;
  const rows = candidate => [candidate.rowNumber, ...(candidate.sourceThreads || []).map(source => source.rowNumber)]
    .filter(row => row !== undefined && row !== null);
  if (rowNumber !== undefined && rowNumber !== null && threads.some(candidate => rows(candidate).length)) {
    return rows(thread).some(row => String(row) === String(rowNumber));
  }

  return threadSubjectMatchesRowAnchor(thread.subject, rowAnchor, threads);
}

export function findNotificationForThread(thread, clientNotifs = [], preferredNotification = null, threads = [thread]) {
  if (!thread) return null;

  const actionableNotifs = clientNotifs.filter(notif => isActionableNotification(notif) && !isCampaignLevelActionNotification(notif));
  const preferred = preferredNotification?.id
    ? actionableNotifs.find(notif => notif.id === preferredNotification.id)
    : null;

  if (preferred && notificationMatchesThread(preferred, thread, threads)) {
    return preferred;
  }

  return selectPreferredCurrentActionNotification(
    actionableNotifs.filter(notif => notificationMatchesThread(notif, thread, threads))
  );
}

function getNotificationThreadStatus(notification, thread) {
  const sourceId = notification?.threadId || notification?.meta?.threadId;
  const source = thread.sourceThreads?.find(candidate => candidate.id === sourceId);
  return source?.status || thread.status;
}

export function buildStoppedThreadUpdate(actorUid) {
  return {
    status: 'stopped',
    followUpStatus: 'stopped',
    statusReason: 'user_requested',
    stoppedAt: serverTimestamp(),
    stoppedBy: actorUid || null,
    updatedAt: serverTimestamp()
  };
}

export function buildStopActionAuditPayload({ uid, client, thread, threadId }) {
  return {
    actorUid: uid,
    actionType: 'stop_conversation',
    status: 'stopped',
    source: 'dashboard_stop_conversation',
    clientId: client?.id || null,
    clientName: client?.name || client?.clientName || client?.companyName || null,
    notificationId: null,
    threadId: threadId || thread?.threadIds?.[0] || thread?.id || null,
    rowAnchor: null,
    reason: 'user_requested',
    subject: thread?.subject || '',
    finalBody: '',
    finalRecipients: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stoppedAt: serverTimestamp()
  };
}

export function getStopConversationApiBaseUrl() {
  return null;
}

export function buildStopConversationFunctionPayload({ client, threadIds, actorUid }) {
  return {
    clientId: client?.id || null,
    threadIds,
    actorUid: actorUid || null
  };
}

function toDateValue(value) {
  if (!value) return new Date(0);
  return value.toDate?.() || new Date(value);
}

export function resolveGroupedThreadStatus(threads = []) {
  if (!threads.length) return 'completed';

  const latest = [...threads].sort((a, b) => toDateValue(b.updatedAt) - toDateValue(a.updatedAt))[0];
  const status = latest?.status || 'active';
  return status === 'closed' ? 'completed' : status;
}

export function messageBodyNeedsEmailSurface(body) {
  const rawContent = typeof body === 'object' ? body?.content : body;
  return isProfessionalSignatureHtml(String(rawContent || ''));
}

function messageTimestampValue(message = {}) {
  const value = message.sentDateTime || message.receivedDateTime || message.createdAt;
  if (!value) return '';
  if (typeof value.toMillis === 'function') return String(value.toMillis());
  if (typeof value.toDate === 'function') return String(value.toDate().getTime());
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

function messageMinuteBucket(message = {}) {
  const value = message.sentDateTime || message.receivedDateTime || message.createdAt;
  if (!value) return '';
  const timestamp = typeof value.toMillis === 'function'
    ? value.toMillis()
    : (typeof value.toDate === 'function' ? value.toDate().getTime() : new Date(value).getTime());
  if (!Number.isFinite(timestamp)) return messageTimestampValue(message);
  return String(Math.floor(timestamp / 60000));
}

function messageTimestampMillis(message = {}) {
  const value = message.sentDateTime || message.receivedDateTime || message.createdAt;
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function messageBodyText(value) {
  const raw = typeof value === 'object' ? value?.content : value;
  return String(raw || '').replace(/\s+/g, ' ').trim();
}

function messageVisibleBodyText(value) {
  const display = getMessageDisplayParts(value);
  const html = display.html || '';
  if (typeof document === 'undefined') {
    return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const container = document.createElement('div');
  container.innerHTML = html;
  return (container.textContent || '').replace(/\s+/g, ' ').trim();
}

function messageCoreReplyText(message = {}) {
  const visibleBody = messageVisibleBodyText(message.body) || messageBodyText(message.body);
  const withoutQuote = String(visibleBody || '').split(/\b(?:From|On)\s*:/i)[0];
  const withoutSignature = withoutQuote.split(/\bBest,?/i)[0];
  return withoutSignature
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s:.']/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function messageParticipants(message = {}) {
  const values = [message.from, message.sender, message.to, message.recipients]
    .flat()
    .filter(Boolean)
    .map(value => String(value).toLowerCase().trim());
  return values.sort().join(',');
}

function messageRecipientSet(message = {}) {
  return new Set([message.to, message.recipients, message.recipient]
    .flat()
    .filter(Boolean)
    .map(value => String(value).toLowerCase().trim()));
}

function messagesShareRecipient(a = {}, b = {}) {
  const aRecipients = messageRecipientSet(a);
  const bRecipients = messageRecipientSet(b);
  if (!aRecipients.size || !bRecipients.size) return true;
  return [...aRecipients].some(value => bRecipients.has(value));
}

function isDashboardReplyPlaceholder(message = {}) {
  return message.source === 'dashboard_outbox_reply' ||
    String(message.id || '').startsWith('dashboard-reply-');
}

function providerIdentity(message = {}) {
  return message.internetMessageId ||
    message.messageId ||
    message.graphMessageId ||
    message.outlookMessageId ||
    message.msGraphMessageId ||
    (!isDashboardReplyPlaceholder(message) ? message.id : null);
}

function shouldReplaceDuplicateMessage(existing = {}, incoming = {}) {
  return isDashboardReplyPlaceholder(existing) &&
    !isDashboardReplyPlaceholder(incoming) &&
    Boolean(providerIdentity(incoming));
}

function isDashboardPlaceholderGraphDuplicate(existing = {}, incoming = {}) {
  const hasPlaceholderPair = isDashboardReplyPlaceholder(existing) || isDashboardReplyPlaceholder(incoming);
  const hasProviderPair = Boolean(providerIdentity(existing) || providerIdentity(incoming));
  if (!hasPlaceholderPair || !hasProviderPair) return false;
  if ((existing.direction || '') !== 'outbound' || (incoming.direction || '') !== 'outbound') return false;
  if (!messagesShareRecipient(existing, incoming)) return false;

  const existingTime = messageTimestampMillis(existing);
  const incomingTime = messageTimestampMillis(incoming);
  if (Number.isFinite(existingTime) && Number.isFinite(incomingTime) && Math.abs(existingTime - incomingTime) > 120000) {
    return false;
  }

  const existingCore = messageCoreReplyText(existing);
  const incomingCore = messageCoreReplyText(incoming);
  if (!existingCore || !incomingCore) return false;

  const shorter = existingCore.length <= incomingCore.length ? existingCore : incomingCore;
  const longer = existingCore.length > incomingCore.length ? existingCore : incomingCore;
  const minimumCore = shorter.slice(0, Math.min(shorter.length, 180));
  return minimumCore.length >= 80 && longer.includes(minimumCore);
}

export function dedupeConversationMessages(messages = []) {
  const result = [];
  const seenKeys = new Map();

  messages.forEach(message => {
    const graphIdentity = providerIdentity(message);
    const providerKey = graphIdentity ? `graph:${String(graphIdentity).toLowerCase().trim()}` : '';
    const normalizedBody = messageBodyText(message.body);
    const visibleBody = messageVisibleBodyText(message.body);
    const exactKey = [
      message.direction || '',
      messageTimestampValue(message),
      messageParticipants(message),
      normalizedBody
    ].join('|');
    const renderedKey = [
      message.direction || '',
      messageMinuteBucket(message),
      visibleBody || normalizedBody
    ].join('|');
    const hasExactKey = exactKey.replace(/\|/g, '').trim();
    const hasRenderedKey = renderedKey.replace(/\|/g, '').trim();
    const keys = [
      providerKey || null,
      hasExactKey ? `exact:${exactKey}` : null,
      hasRenderedKey ? `rendered:${renderedKey}` : null
    ].filter(Boolean);

    if (!keys.length) {
      result.push(message);
      return;
    }

    const existingIndex = keys
      .map(key => seenKeys.get(key))
      .find(index => Number.isInteger(index));
    const placeholderDuplicateIndex = Number.isInteger(existingIndex)
      ? existingIndex
      : result.findIndex(existingMessage => isDashboardPlaceholderGraphDuplicate(existingMessage, message));
    const duplicateIndex = Number.isInteger(existingIndex) ? existingIndex : placeholderDuplicateIndex;

    if (duplicateIndex >= 0) {
      if (shouldReplaceDuplicateMessage(result[duplicateIndex], message)) {
        result[duplicateIndex] = message;
        keys.forEach(key => seenKeys.set(key, duplicateIndex));
      }
      return;
    }

    const nextIndex = result.length;
    result.push(message);
    keys.forEach(key => seenKeys.set(key, nextIndex));
  });

  return result;
}

function MessageBody({ body, className = 'conv-message-body' }) {
  const display = getMessageDisplayParts(body);
  const bodyClassName = messageBodyNeedsEmailSurface(body)
    ? `${className} conv-message-body--email-surface`
    : className;

  return (
    <div className={bodyClassName}>
      <div dangerouslySetInnerHTML={{ __html: display.html }} />
      {display.wasTrimmed && display.rawText && (
        <details className="conv-message-raw">
          <summary>Show full message</summary>
          <pre>{display.rawText}</pre>
        </details>
      )}
    </div>
  );
}

/**
 * Inline expandable panel to display email conversations for a client.
 * Replaces the modal approach with a folder-like nested view.
 */
export default function ConversationsPanel({
  client,
  onLoaded,
  autoScrollToNotification,
  onAutoScrollHandled,
  externalActionNotification,
  onExternalActionComplete
}) {
  // Ref for scrolling to specific elements
  const actionElementRefs = useRef({});
  // Ref for action input areas (keyed by notification ID)
  const actionInputRefs = useRef({});
  // Ref for the pending section (to scroll to after sending new property email)
  const pendingSectionRef = useRef(null);
  const onLoadedRef = useRef(onLoaded);
  const [threads, setThreads] = useState([]);
  const [pendingOutbox, setPendingOutbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [manualReplyThreadId, setManualReplyThreadId] = useState(null);
  const [expandedPending, setExpandedPending] = useState(true);
  const [expandedPendingItems, setExpandedPendingItems] = useState({});
  const [error, setError] = useState(null);
  const [cancellingIds, setCancellingIds] = useState(new Set());
  const [stoppingIds, setStoppingIds] = useState(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedActionNotification, setSelectedActionNotification] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  const [resolvingIds, setResolvingIds] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);

  // Get notifications from context to find action_needed for paused threads
  const { clientNotifications, clientThreads } = useNotificationsContext();

  // Get email signature from session storage (already cached)
  const { sessionData } = useSessionStorage();
  const signatureMode = sessionData.signatureMode || sessionData.userProfile?.signatureMode;
  const emailSignature = useMemo(() => buildActiveSignatureFromProfile({
    ...(sessionData.userProfile || {}),
    emailSignature: sessionData.emailSignature ?? sessionData.userProfile?.emailSignature,
    signatureMode
  }, sessionData.organizationName), [sessionData.userProfile, sessionData.emailSignature, signatureMode, sessionData.organizationName]);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  useEffect(() => {
    if (autoScrollToNotification) {
      setSelectedActionNotification(autoScrollToNotification);
    }
  }, [autoScrollToNotification]);

  // Fetch threads and pending outbox for this client
  useEffect(() => {
    async function fetchData() {
      const user = auth.currentUser;
      if (!user || !client?.id) {
        setLoading(false);
        onLoadedRef.current?.();
        return;
      }

      try {
        const threadsRef = collection(db, 'users', user.uid, 'threads');
        const outboxRef = collection(db, 'users', user.uid, 'outbox');

        const threadsQuery = query(
          threadsRef,
          where('clientId', '==', client.id),
          orderBy('updatedAt', 'desc')
        );

        // Note: No orderBy to avoid needing composite index - we sort client-side
        const outboxQuery = query(
          outboxRef,
          where('clientId', '==', client.id)
        );

        const [threadsSnapshot, outboxSnapshot] = await Promise.all([
          getDocs(threadsQuery),
          getDocs(outboxQuery)
        ]);

        // Process outbox items and sort client-side
        const outboxItems = outboxSnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })).filter(item => isPendingOutboxItem(item) && !isNextVersionSchedulingRecord(item)).sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const timeB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return timeA - timeB;
        });
        setPendingOutbox(outboxItems);

        // Process threads
        const threadList = [];
        for (const docSnap of threadsSnapshot.docs) {
          const threadData = docSnap.data();
          if (isNextVersionSchedulingRecord(threadData)) continue;

          // Fetch messages for this thread
          const messagesRef = collection(db, 'users', user.uid, 'threads', docSnap.id, 'messages');
          const messagesQuery = query(messagesRef, orderBy('sentDateTime', 'asc'));
          const messagesSnapshot = await getDocs(messagesQuery);

          const messages = messagesSnapshot.docs.map(msgDoc => ({
            id: msgDoc.id,
            ...msgDoc.data()
          }));

          threadList.push({
            id: docSnap.id,
            ...threadData,
            messages
          });
        }

        // Group threads by property address
        const groupedByProperty = {};
        for (const thread of threadList) {
          const propertyKey = getThreadDisplaySubject(thread.subject);
          const normalizedKey = getThreadPropertyKey(thread.subject);

          if (!groupedByProperty[normalizedKey]) {
            groupedByProperty[normalizedKey] = {
              propertyAddress: normalizedKey,
              displaySubject: propertyKey,
              threads: [],
              allMessages: [],
              latestUpdate: null,
              emails: new Set(),
              status: 'completed'
            };
          }

          groupedByProperty[normalizedKey].threads.push(thread);
          groupedByProperty[normalizedKey].allMessages.push(...thread.messages);

          if (!groupedByProperty[normalizedKey].latestUpdate ||
              (thread.updatedAt && thread.updatedAt > groupedByProperty[normalizedKey].latestUpdate)) {
            groupedByProperty[normalizedKey].latestUpdate = thread.updatedAt;
          }

          if (thread.email) {
            const emailList = Array.isArray(thread.email) ? thread.email : [thread.email];
            emailList.forEach(e => groupedByProperty[normalizedKey].emails.add(e));
          }

          groupedByProperty[normalizedKey].status = resolveGroupedThreadStatus(groupedByProperty[normalizedKey].threads);
        }

        const groupedList = Object.values(groupedByProperty).map(group => ({
          id: group.propertyAddress,
          subject: group.displaySubject,
          email: Array.from(group.emails),
          messages: dedupeConversationMessages(group.allMessages.sort((a, b) => {
            const timeA = a.sentDateTime?.toDate?.() || new Date(a.sentDateTime || 0);
            const timeB = b.sentDateTime?.toDate?.() || new Date(b.sentDateTime || 0);
            return timeA - timeB;
          })),
          updatedAt: group.latestUpdate,
          threadCount: group.threads.length,
          status: resolveGroupedThreadStatus(group.threads),
          // Carried so the Reply control can tell an opted-out conversation
          // from one that is simply finished.
          optedOutAt: group.threads.find(t => t.optedOutAt)?.optedOutAt || null,
          rowNumber: group.threads.find(t => t.rowNumber != null)?.rowNumber ?? null,
          threadIds: group.threads.map(t => t.id),
          sourceThreads: group.threads.map(thread => ({
            id: thread.id,
            status: thread.status,
            contactName: thread.contactName ?? null,
            rowNumber: thread.rowNumber ?? null,
            email: thread.email,
            messages: thread.messages || []
          }))
        }));

        groupedList.sort((a, b) => {
          const timeA = a.updatedAt?.toDate?.() || new Date(a.updatedAt || 0);
          const timeB = b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0);
          return timeB - timeA;
        });

        setThreads(groupedList);

        // Auto-expand the first thread if there's only one
        if (groupedList.length === 1) {
          setExpandedThreads({ [groupedList[0].id]: true });
        }
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        onLoadedRef.current?.();
      }
    }

    fetchData();
  }, [client?.id, refreshKey]);

  // Handle auto-scroll to a specific notification/action
  useEffect(() => {
    if (!autoScrollToNotification || loading) return;

    if (isCampaignLevelActionNotification(autoScrollToNotification)) {
      actionInputRefs.current[autoScrollToNotification.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onAutoScrollHandled?.();
      return;
    }

    // Find the thread that matches this notification
    const notif = autoScrollToNotification;
    let matchingThreadId = null;

    for (const thread of threads) {
      if (notificationMatchesThread(notif, thread, threads)) {
        matchingThreadId = thread.id;
        break;
      }
    }

    if (matchingThreadId) {
      // Auto-expand the matching thread
      setExpandedThreads(prev => ({ ...prev, [matchingThreadId]: true }));

      // Scroll to the action input element after a short delay for DOM to update
      setTimeout(() => {
        // Try to scroll to the specific action input first (by notification ID)
        const actionInputEl = actionInputRefs.current[notif.id];
        if (actionInputEl) {
          actionInputEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fall back to scrolling to the thread
          const threadEl = actionElementRefs.current[matchingThreadId];
          if (threadEl) {
            threadEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 200); // Slightly longer delay to ensure action elements render
    }

    // Clear the auto-scroll state
    onAutoScrollHandled?.();
  }, [autoScrollToNotification, loading, threads, onAutoScrollHandled]);

  // Handle new property email sent - scroll to pending section at top
  const handleNewPropertySent = useCallback(() => {
    // Ensure pending section is expanded so the new message is visible
    setExpandedPending(true);
    // Trigger data refresh
    setRefreshKey(k => k + 1);
    // Scroll to pending section after DOM updates
    setTimeout(() => {
      if (pendingSectionRef.current) {
        pendingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300); // Allow time for Firestore fetch and DOM update
  }, []);

  // Handle thread reply sent - just refresh, stay in the conversation
  const handleThreadReplySent = useCallback(() => {
    // Just refresh data - the pending reply will appear at bottom of the thread
    // User is already viewing the conversation, no need to scroll away
    setRefreshKey(k => k + 1);
    onExternalActionComplete?.();
  }, [onExternalActionComplete]);

  // Get all notifications for this client (for inline display)
  const allClientNotifs = useMemo(() => {
    const stored = (clientNotifications[client?.id]?.notifications || [])
      .filter(notif => !isNextVersionSchedulingRecord(notif));
    if (!externalActionNotification) return stored;
    return [externalActionNotification, ...stored.filter(notif => notif.id !== externalActionNotification.id)];
  }, [clientNotifications, client?.id, externalActionNotification]);

  // R14 5.1: the header counts every open card for the campaign, so the
  // attention list has to show every one of them. The dashboard counts through
  // isRenderableDashboardActionNotification against the campaign's own thread
  // lookup; count the list the same way, then render campaign-level cards as
  // the full cards they already were and conversation-level cards as one line
  // each that opens the conversation they live in.
  const attentionNotifications = useMemo(() => {
    const lookupById = clientThreads || {};
    const threadOrderLookup = client?.id && Object.prototype.hasOwnProperty.call(lookupById, client.id)
      ? lookupById[client.id]
      : undefined;
    return allClientNotifs.filter(notif =>
      isRenderableDashboardActionNotification(notif, threadOrderLookup));
  }, [allClientNotifs, clientThreads, client?.id]);

  const campaignNotifications = attentionNotifications.filter(isCampaignLevelActionNotification);
  const conversationNotifications = attentionNotifications.filter(notif =>
    !isCampaignLevelActionNotification(notif));

  const handleResolveActionNotification = useCallback(async (thread, notification) => {
    const user = auth.currentUser;
    if (!user || !client?.id || !notification?.id) return;

    try {
      const deadLetterId = notification.meta?.deadLetterId;
      if (deadLetterId) {
        await updateDoc(doc(db, 'users', user.uid, 'deadLetterQueue', deadLetterId), {
          status: 'discarded',
          recoveryStatus: 'discarded',
          resolution: 'mark_handled',
          resolvedAt: serverTimestamp(),
          resolvedBy: user.uid,
          updatedAt: serverTimestamp()
        });
        setResolveError(null);
        setSelectedActionNotification(prev => prev?.id === notification.id ? null : prev);
        onExternalActionComplete?.();
        return;
      }

      // Mark Handled resolves only this item: nothing is sent, the hold clears,
      // and the conversation stays paused until every open card on it is gone.
      // Dismiss (the Stop control) is what ends a conversation, so the two must
      // not share a path.
      //
      // This runs entirely on the client, the way every other card decision
      // that works in production does (InlineNewPropertyCard's handleDismiss):
      // audit row, then the counter-safe delete. It used to POST
      // /api/dismiss-notification, and that route is deployed nowhere a browser
      // can reach - the host it was aimed at is the sheet-creation function,
      // which has no router and answered "Missing required fields" to every
      // click (DEF-13).
      const threadId = notification.threadId || notification.meta?.threadId || thread?.threadIds?.[0] || thread?.id || null;

      // 1. Cancel any send parked behind this card FIRST, the way the
      //    pending-reply control cancels one. A queued send must not leave
      //    after the advisor has said the item is dealt with - and if it
      //    cannot be stopped, nothing else may happen either. Resolving the
      //    card anyway would leave a send on its way behind an item that is no
      //    longer on screen, which is the one state the advisor cannot see or
      //    act on. So this runs before any write, and a refusal aborts the
      //    whole decision with everything exactly as it was.
      const parkedSends = await getDocs(query(
        collection(db, 'users', user.uid, 'outbox'),
        where('notificationId', '==', notification.id)
      ));
      for (const parked of parkedSends.docs) {
        if (!isPendingOutboxItem({ id: parked.id, ...parked.data() })) continue;
        try {
          await updateDoc(doc(db, 'users', user.uid, 'outbox', parked.id), {
            cancelRequested: true,
            status: 'cancel_requested',
            cancelledAt: serverTimestamp()
          });
        } catch (cancelErr) {
          console.error('Could not cancel the send parked behind this item:', cancelErr);
          throw new Error(PARKED_SEND_UNCANCELLABLE_MESSAGE);
        }
      }

      // 2. Determine whether another visible decision still owns the pause.
      //    This read happens after every parked send has been cancelled and
      //    before the one transaction that resolves the card and its exact
      //    source-bound PDF hold.
      let otherActionsForThread = false;
      let preQueryThreadData = null;
      const threadRef = threadId ? doc(db, 'users', user.uid, 'threads', threadId) : null;
      if (threadId) {
        const preQueryThreadSnapshot = await getDoc(threadRef);
        preQueryThreadData = preQueryThreadSnapshot.exists()
          ? (preQueryThreadSnapshot.data() || {})
          : null;
        const threadNotifications = await getDocs(query(
          collection(db, 'users', user.uid, 'clients', client.id, 'notifications'),
          where('threadId', '==', threadId)
        ));
        otherActionsForThread = threadNotifications.docs.some(docSnap => (
          docSnap.id !== notification.id &&
          isActionableNotification({ id: docSnap.id, ...docSnap.data() })
        ));
      }

      // 3. Resolve exactly this card and update the thread in one transaction.
      //    The PDF hold is authority-bearing backend state: a generic or stale
      //    card cannot clear it. The backend stamps the exact warning key into
      //    the matching PDF card, and the transaction re-reads the current
      //    thread before comparing the two opaque, case-sensitive values.
      const clientRef = doc(db, 'users', user.uid, 'clients', client.id);
      const notificationRef = doc(db, 'users', user.uid, 'clients', client.id, 'notifications', notification.id);
      const auditRef = doc(collection(db, 'users', user.uid, 'actionAudit'));
      const pendingResponseRef = threadId
        ? doc(db, 'users', user.uid, 'pendingResponses', threadId)
        : null;
      const deadLetterRef = pendingResponseRef
        ? doc(collection(db, 'users', user.uid, 'deadLetterQueue'))
        : null;
      const handled = await runTransaction(db, async transaction => {
        const notificationSnapshot = await transaction.get(notificationRef);
        if (!notificationSnapshot.exists()) return false;
        const clientSnapshot = await transaction.get(clientRef);
        const threadSnapshot = threadRef ? await transaction.get(threadRef) : null;
        const pendingResponseSnapshot = pendingResponseRef
          ? await transaction.get(pendingResponseRef)
          : null;
        const notificationData = notificationSnapshot.data() || {};
        if (!clientSnapshot.exists()) {
          throw new Error('This campaign changed before the item could be marked handled. Refresh and try again.');
        }
        if (threadRef && !threadSnapshot?.exists()) {
          throw new Error('This conversation changed before the item could be marked handled. Refresh and try again.');
        }
        const persistedThreadId = notificationData.threadId || notificationData.meta?.threadId || null;
        const persistedClientId = notificationData.clientId || notificationData.meta?.clientId || null;
        if (
          persistedThreadId !== threadId ||
          (persistedClientId && persistedClientId !== client.id)
        ) {
          throw new Error('This item changed before it could be marked handled. Refresh and try again.');
        }
        const clientData = clientSnapshot.data() || {};

        const auditPayload = buildActionAuditPayload({
          uid: user.uid,
          actionType: MARK_HANDLED_ACTION_TYPE,
          status: MARK_HANDLED_AUDIT_STATUS,
          source: MARK_HANDLED_AUDIT_SOURCE,
          client,
          notification: { id: notification.id, ...notificationData },
          threadId,
          rowAnchor: notificationData.rowAnchor ?? notificationData.meta?.rowAnchor ?? null,
          rowNumber: notificationData.rowNumber ?? notificationData.meta?.rowNumber ?? null,
          reason: notificationData.meta?.reason ?? notificationData.reason ?? null,
          subject: '',
          suggestedBody: '',
          suggestedRecipients: [],
          suggestedCcRecipients: [],
          finalBody: '',
          finalRecipients: [],
          finalCcRecipients: [],
          sourceMessageId: notificationData.meta?.sourceMessageId ?? notificationData.sourceMessageId ?? null,
          handledAt: serverTimestamp()
        });

        transaction.delete(notificationRef);
        transaction.set(auditRef, auditPayload);
        transaction.set(
          clientRef,
          buildNotificationCounterUpdate(clientData, notificationData.kind || notificationData.type || null),
          { merge: true }
        );

        if (threadRef && threadSnapshot?.exists()) {
          const threadData = threadSnapshot.data() || {};
          const cardWarningKey = replacementReviewWarningKey(
            notification.id,
            notificationData
          );
          const threadWarningKey = threadData.pdfManualReviewWarningKey;
          const cardReason = notificationData.meta?.reason || notificationData.reason || '';
          const cardThreadId = notificationData.threadId || notificationData.meta?.threadId || null;
          const cardClientId = notificationData.clientId || notificationData.meta?.clientId || null;
          const cardOwnsTargetScope = (
            cardThreadId === threadId &&
            threadData.clientId === client.id &&
            (!cardClientId || cardClientId === client.id) &&
            PDF_MANUAL_REVIEW_REASONS.has(cardReason)
          );
          const matchesCurrentPdfHold = (
            cardOwnsTargetScope &&
            typeof cardWarningKey === 'string' &&
            cardWarningKey.length > 0 &&
            cardWarningKey === threadWarningKey
          );
          const hasPdfHold = (
            threadData.pdfManualReviewRequired === true ||
            threadData.pdfManualReviewCleanupRequired === true
          );
          const threadPatch = {};
          const threadGenerationUnchanged = (
            preQueryThreadData !== null &&
            threadResolutionGeneration(preQueryThreadData) === threadResolutionGeneration(threadData)
          );

          if (matchesCurrentPdfHold) {
            if (pendingResponseSnapshot?.exists()) {
              const pendingData = pendingResponseSnapshot.data() || {};
              const pendingOwnsTargetScope = (
                pendingData.threadId === threadId &&
                pendingData.clientId === client.id &&
                pendingData.status === 'held_for_pdf_manual_review' &&
                pendingData.pdfManualReviewRequired === true &&
                sourceTurnIdentitiesIntersect(notificationData, pendingData)
              );
              if (!pendingOwnsTargetScope) {
                throw new Error(PARKED_PDF_DRAFT_UNCANCELLABLE_MESSAGE);
              }
              transaction.set(deadLetterRef, {
                ...pendingData,
                source: 'pendingResponses',
                originalDocId: threadId,
                failureReason: (
                  'Pending response stopped because the advisor manually continued ' +
                  'this PDF-review conversation; review before retrying the stale draft.'
                ),
                status: 'discarded',
                recoveryStatus: 'discarded',
                resolution: 'mark_handled',
                resolvedAt: serverTimestamp(),
                resolvedBy: user.uid,
                deadLetteredAt: serverTimestamp(),
                movedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
              transaction.delete(pendingResponseRef);
            }
            Object.assign(threadPatch, {
              pdfManualReviewRequired: false,
              pdfManualReviewWarningKey: null,
              pdfManualReviewCleanupRequired: false,
              pdfManualReviewResolvedAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }

          if (
            !otherActionsForThread &&
            threadGenerationUnchanged &&
            threadData.status === 'paused' &&
            !threadHasTerminalDisposition(threadData) &&
            (!hasPdfHold || matchesCurrentPdfHold)
          ) {
            Object.assign(threadPatch, {
              status: 'active',
              statusReason: 'user_marked_handled',
              statusUpdatedAt: serverTimestamp(),
              followUpStatus: 'waiting',
              'followUpConfig.processingBy': null,
              'followUpConfig.processingAt': null,
              advisorReviewSourceMessageId: null,
              advisorReviewSourceInternetMessageId: null,
              advisorReviewSourceStatusReason: null,
              updatedAt: serverTimestamp()
            });
          }

          if (Object.keys(threadPatch).length > 0) {
            // update() interprets the two dotted followUpConfig keys as field
            // paths. set(..., {merge:true}) would create literal dotted keys
            // and leave the real processing lease untouched.
            transaction.update(threadRef, threadPatch);
          }
        }
        return true;
      });
      if (!handled) {
        throw new Error('This item changed before it could be marked handled. Refresh and try again.');
      }

      setResolveError(null);
      setSelectedActionNotification(prev => prev?.id === notification.id ? null : prev);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to mark action handled:', err);
      // The item stays on screen: it was not handled. A 409
      // handled_record_unprovable means the advisor should Dismiss or reply.
      setResolveError({
        notificationId: notification.id,
        message: err?.message || 'Failed to mark this action handled. Please try again.'
      });
    }
  }, [client, onExternalActionComplete]);

  // Split pending outbox: items with threadId go in their thread, others go in global pending
  const { globalPendingOutbox, threadPendingMap } = useMemo(() => {
    const global = [];
    const threadMap = {}; // threadId -> [items]

    for (const item of pendingOutbox) {
      if (item.threadId) {
        if (!threadMap[item.threadId]) {
          threadMap[item.threadId] = [];
        }
        threadMap[item.threadId].push(item);
      } else {
        global.push(item);
      }
    }

    return { globalPendingOutbox: global, threadPendingMap: threadMap };
  }, [pendingOutbox]);

  // Find notifications triggered by a specific message
  // Matches by: threadId, timestamp window, or property address
  const getNotificationsForMessage = useCallback((message, nextMessage, thread, isLastMessage) => {
    const reactions = [];
    // R14 3.7: one broker reply can carry several attached flyers, each producing
    // its own new_property_pending_approval notification. Collect every one of
    // them so the conversation renders a card per suggested suite (previously the
    // scalar meant the last matching notification silently won).
    const newPropertyNotifs = [];

    if (!message) return { reactions, newPropertyNotifs };

    const isInbound = message?.direction === 'inbound';
    const isOutbound = message?.direction === 'outbound';

    // Only show notifications after INBOUND messages (broker replies trigger notifications)
    // Exception: If this is the LAST message AND it's outbound, show notifications
    // that were created AFTER this outbound (like row_completed after closing email)
    if (!isInbound && !(isLastMessage && isOutbound)) {
      return { reactions, newPropertyNotifs };
    }

    const messageTime = message.sentDateTime?.toDate?.() ||
                       message.receivedDateTime?.toDate?.() ||
                       new Date(message.sentDateTime || message.receivedDateTime || 0);

    // Determine the time window for this message
    let startTime = messageTime;
    let endTime;

    if (isInbound) {
      // For inbound: show notifications from this message until the next message
      endTime = nextMessage
        ? (nextMessage.sentDateTime?.toDate?.() ||
           nextMessage.receivedDateTime?.toDate?.() ||
           new Date(nextMessage.sentDateTime || nextMessage.receivedDateTime || 0))
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    } else {
      // For last outbound: only show notifications created AFTER this message
      // (to catch row_completed that comes after closing email)
      startTime = messageTime;
      endTime = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    }

    // Find notifications that belong to this message's time window
    allClientNotifs.forEach(notif => {
      const notifTime = notif.createdAt?.toDate?.() ||
                       new Date(notif.createdAt?.seconds * 1000 || 0);

      if (!notificationMatchesThread(notif, thread, threads)) return;

      // Check timestamp - notification should be in this message's time window
      // For inbound messages: allow 5 second buffer before start time
      // For last outbound: only show notifications created STRICTLY after the message
      const bufferMs = isInbound ? 5000 : 0;
      if (notifTime < new Date(startTime.getTime() - bufferMs)) return;
      if (notifTime > endTime) return;

      // Handle new property notifications separately
      if (notif.meta?.reason === 'new_property_pending_approval') {
        newPropertyNotifs.push(notif);
        return;
      }

      reactions.push(notif);
    });

    // Sort reactions by time
    reactions.sort((a, b) => {
      const timeA = a.createdAt?.toDate?.() || new Date(0);
      const timeB = b.createdAt?.toDate?.() || new Date(0);
      return timeA - timeB;
    });

    return { reactions, newPropertyNotifs };
  }, [allClientNotifs, threads]);

  // Get notification display info
  const getNotificationDisplay = (notif) => {
    switch (notif.kind) {
      case 'sheet_update':
        return {
          icon: FileText,
          label: `Updated: ${notif.meta?.column || 'Field'}`,
          value: notif.meta?.newValue || '',
          color: '#10b981',
          bgColor: 'rgba(16, 185, 129, 0.1)'
        };
      case 'row_completed':
        return {
          icon: CheckCircle,
          label: 'Property Complete',
          value: 'All required fields filled',
          color: '#10b981',
          bgColor: 'rgba(16, 185, 129, 0.1)'
        };
      case 'property_unavailable':
        return {
          icon: Ban,
          label: 'Property Unavailable',
          value: 'Moved to non-viable',
          color: '#ef4444',
          bgColor: 'rgba(239, 68, 68, 0.1)'
        };
      case 'action_needed':
        const reason = notif.meta?.reason || '';
        const tourActionLabel = getTourActionLabel(reason);

        if (tourActionLabel) {
          return {
            icon: Clock,
            label: tourActionLabel,
            value: '',
            color: '#2563eb',
            bgColor: 'rgba(37, 99, 235, 0.1)'
          };
        }

        // Needs user input (various subreasons)
        // Note: Don't show the question text as value - it's already visible in the broker's message
        if (reason.startsWith('needs_user_input:')) {
          const subreason = reason.split(':')[1] || 'unclear';
          const labels = {
            'reply_withheld_broker_promised_fields': 'Reply Held',
            'confidential': 'Identity Question',
            'client_question': 'Client Question',
            'scheduling': 'Scheduling Request',
            'negotiation': 'Negotiation',
            'legal_contract': 'Contract Question',
            'unclear': 'Input Needed'
          };
          return {
            icon: AlertTriangle,
            // Every reason the live client actually raises - the attachment
            // family, the unmatched reply, the multi-property flyer - is absent
            // from `labels` above, so the chip read a bare "Input Needed" and
            // said nothing at all (DEF-11). The shared map names them.
            label: labels[subreason] || getNeedsUserInputLabel(reason) || 'Input Needed',
            // Held replies and delivery reports explain what happened to our outbound email.
            value: ['email_not_delivered', 'email_delayed'].includes(subreason)
              ? getActionCardDetails(notif) || ''
              : subreason === 'reply_withheld_broker_promised_fields' ? notif.meta?.details || '' : '',
            color: '#f59e0b',
            bgColor: 'rgba(245, 158, 11, 0.1)'
          };
        }

        // Call requested - only show phone number if available
        if (reason === 'call_requested') {
          return {
            icon: Phone,
            label: 'Call Requested',
            value: notif.meta?.phoneNumber || '', // Only show if we extracted a phone number
            color: '#8b5cf6',
            bgColor: 'rgba(139, 92, 246, 0.1)'
          };
        }

        // Contact opt-out - label is descriptive enough
        if (reason.startsWith('contact_optout:')) {
          const subreason = reason.split(':')[1] || '';
          const labels = {
            'not_interested': 'Not Interested',
            'unsubscribe': 'Unsubscribe Request',
            'do_not_contact': 'Do Not Contact',
            'no_tenant_reps': 'No Tenant Reps',
            'direct_only': 'Direct Only',
            'hostile': 'Hostile Response'
          };
          return {
            icon: UserMinus,
            label: labels[subreason] || 'Opt-Out',
            value: '', // Label is descriptive enough, message shows details
            color: '#ef4444',
            bgColor: 'rgba(239, 68, 68, 0.1)'
          };
        }

        // Wrong contact - only show suggested contact if available
        if (reason.startsWith('wrong_contact:')) {
          const subreason = reason.split(':')[1] || '';
          const labels = {
            'no_longer_handles': 'No Longer Handles',
            'wrong_person': 'Wrong Person',
            'forwarded': 'Forwarded',
            'left_company': 'Left Company'
          };
          return {
            icon: UserX,
            label: labels[subreason] || 'Wrong Contact',
            value: notif.meta?.suggestedContact || '', // Only show if we have a suggested contact
            color: '#f97316',
            bgColor: 'rgba(249, 115, 34, 0.1)'
          };
        }

        // Property issue
        if (reason.startsWith('property_issue:')) {
          const severity = reason.split(':')[1] || 'minor';
          const labels = {
            'critical': 'Critical Issue',
            'major': 'Major Issue',
            'minor': 'Minor Issue'
          };
          const colors = {
            'critical': '#dc2626',
            'major': '#ea580c',
            'minor': '#f59e0b'
          };
          return {
            icon: AlertOctagon,
            label: labels[severity] || 'Property Issue',
            value: notif.meta?.issue || notif.meta?.details || 'Issue reported',
            color: colors[severity] || '#f59e0b',
            bgColor: `rgba(${severity === 'critical' ? '220, 38, 38' : severity === 'major' ? '234, 88, 12' : '245, 158, 11'}, 0.1)`
          };
        }

        // Missing fields
        if (reason === 'missing_fields') {
          return {
            icon: FileText,
            label: 'Missing Fields',
            value: notif.meta?.details || 'Required fields still missing',
            color: '#6b7280',
            bgColor: 'rgba(107, 114, 128, 0.1)'
          };
        }

        // Fallback for any other action_needed
        return {
          icon: AlertTriangle,
          label: 'Action Needed',
          value: notif.meta?.details || '',
          color: '#f59e0b',
          bgColor: 'rgba(245, 158, 11, 0.1)'
        };
      case 'conversation_closed':
        return {
          icon: MessageSquareOff,
          label: 'Conversation Ended',
          value: 'No further follow-up needed',
          color: '#6b7280',
          bgColor: 'rgba(107, 114, 128, 0.1)'
        };
      default:
        return {
          icon: FileText,
          label: notif.kind,
          value: '',
          color: '#6b7280',
          bgColor: 'rgba(107, 114, 128, 0.1)'
        };
    }
  };

  // Toggle thread expansion
  const toggleThread = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };

  // Stop monitoring a conversation
  const performStopConversation = async (thread) => {
    const user = auth.currentUser;
    if (!user || !thread.threadIds?.length) return;
    const threadIds = thread.threadIds.filter(Boolean);
    setStoppingIds(prev => new Set([...prev, thread.id]));

    const deleteThreadNotifications = async () => {
      if (!client?.id) return;
      const notificationsRef = collection(db, 'users', user.uid, 'clients', client.id, 'notifications');
      for (const threadId of threadIds) {
        const notificationsQuery = query(notificationsRef, where('threadId', '==', threadId));
        const snapshot = await getDocs(notificationsQuery);
        await Promise.all(snapshot.docs.map(docSnap => deleteDoc(docSnap.ref)));
      }
    };

    try {
      let stoppedByFunction = false;
      try {
        const stopConversation = httpsCallable(firebaseFunctions, 'stopConversation');
        await stopConversation(buildStopConversationFunctionPayload({
          client,
          threadIds,
          actorUid: user.uid
        }));
        stoppedByFunction = true;
      } catch (functionErr) {
        console.warn('Firebase stop conversation failed, using Firestore fallback:', functionErr);
      }

      if (!stoppedByFunction) {
        for (const threadId of threadIds) {
          const threadRef = doc(db, 'users', user.uid, 'threads', threadId);
          await updateDoc(threadRef, buildStoppedThreadUpdate(user.uid));
          await addDoc(collection(db, 'users', user.uid, 'actionAudit'), buildStopActionAuditPayload({
            uid: user.uid,
            client,
            thread,
            threadId
          }));
        }
      }

      await deleteThreadNotifications();
      setThreads(prev => prev.map(t =>
        t.id === thread.id ? { ...t, status: 'stopped', followUpStatus: 'stopped' } : t
      ));
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error stopping conversation:', err);
      alert('Failed to stop conversation. Please try again.');
    } finally {
      setStoppingIds(prev => {
        const next = new Set(prev);
        next.delete(thread.id);
        return next;
      });
    }
  };

  const handleStopConversation = (thread, e) => {
    e.stopPropagation();
    setConfirmAction({
      title: `Stop monitoring ${thread.subject}?`,
      description: 'The system will no longer auto-reply or send follow-ups for this property.',
      confirmLabel: 'Stop Monitoring',
      onConfirm: () => performStopConversation(thread)
    });
  };

  // Cancel a pending outbox item
  const performCancelOutbox = async (outboxItem) => {
    const user = auth.currentUser;
    if (!user) return;

    setCancellingIds(prev => new Set([...prev, outboxItem.id]));
    setPendingOutbox(prev => prev.filter(item => item.id !== outboxItem.id));

    try {
      await updateDoc(doc(db, 'users', user.uid, 'outbox', outboxItem.id), {
        cancelRequested: true,
        status: 'cancel_requested',
        cancelledAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error cancelling outbox item:', err);
      setPendingOutbox(prev => [...prev, outboxItem].sort((a, b) => {
        const timeA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const timeB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return timeA - timeB;
      }));
      alert('Failed to cancel. Please try again.');
    } finally {
      setCancellingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(outboxItem.id);
        return newSet;
      });
    }
  };

  const handleCancelOutbox = (outboxItem, e) => {
    e.stopPropagation();
    setConfirmAction({
      title: `Cancel pending email to ${outboxItem.assignedEmails?.[0] || 'broker'}?`,
      description: 'If the worker has not started sending it yet, the email will be removed safely.',
      confirmLabel: 'Cancel Email',
      onConfirm: () => performCancelOutbox(outboxItem)
    });
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get status badge info
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { icon: Timer, label: 'Pending', className: 'status-badge--pending' };
      case 'active':
        return { icon: Play, label: 'Active', className: 'status-badge--active' };
      case 'paused':
        return { icon: Pause, label: 'Paused', className: 'status-badge--paused' };
      case 'stopped':
        return { icon: StopCircle, label: 'Stopped', className: 'status-badge--stopped' };
      case 'completed':
      case 'closed':
        return { icon: CheckCircle, label: 'Completed', className: 'status-badge--completed' };
      default:
        return { icon: Play, label: 'Active', className: 'status-badge--active' };
    }
  };

  // Open the conversation an attention line belongs to and bring its card into
  // view, the same way autoScrollToNotification does from the campaign list.
  const focusConversationNotification = useCallback(notification => {
    const matchingThread = threads.find(thread =>
      notificationMatchesThread(notification, thread, threads));
    if (!matchingThread) return;
    setExpandedThreads(prev => ({ ...prev, [matchingThread.id]: true }));
    setTimeout(() => {
      const target = actionInputRefs.current[notification.id]
        || actionElementRefs.current[matchingThread.id];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [threads]);

  const renderAttentionLine = notification => {
    const label = getNotificationDisplay(notification).label;
    const property = getActionCardPropertyLabel(notification);
    const row = getActionCardRowLabel(notification);
    return (
      <button
        key={notification.id}
        type="button"
        className="conv-attention-line"
        aria-label={`${label}${property ? ` — ${property}` : ''}${row ? ` — ${row}` : ''}`}
        onClick={() => focusConversationNotification(notification)}
      >
        <span className="conv-attention-line-label">{label}</span>
        {property && <span className="conv-attention-line-property">{property}</span>}
        {row && <span className="conv-attention-line-row">{row}</span>}
      </button>
    );
  };

  const renderAttentionCard = (notification, thread = null) => {
    const label = getNotificationDisplay(notification).label;
    const property = getActionCardPropertyLabel(notification);
    const row = getActionCardRowLabel(notification);
    return (
      <article key={notification.id} aria-label={label} className="conv-attention-card"
        ref={el => { actionInputRefs.current[notification.id] = el; }}>
        <h4>{label}</h4>
        {(property || row) && <p className="conv-attention-anchor">
          {property && property !== row && <span>{property}</span>}
          {row && <span>{row}</span>}
        </p>}
        {getActionCardDetails(notification) && <p className="conv-attention-details">{getActionCardDetails(notification)}</p>}
        {resolveError?.notificationId === notification.id && <p className="conv-resolve-error" role="alert">{resolveError.message}</p>}
        <button type="button" className="conv-handle-btn" disabled={resolvingIds.has(notification.id)}
          onClick={async () => {
            setResolvingIds(previous => new Set(previous).add(notification.id));
            try {
              await handleResolveActionNotification(thread, notification);
            } finally {
              setResolvingIds(previous => {
                const next = new Set(previous);
                next.delete(notification.id);
                return next;
              });
            }
          }}>Mark Handled</button>
      </article>
    );
  };

  if (loading) {
    return (
      <div className="conversations-panel conversations-panel--loading">
        <Loader size={20} className="spinning" />
        <span>Loading conversations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversations-panel conversations-panel--error">
        Error loading conversations: {error}
      </div>
    );
  }

  if (threads.length === 0 && pendingOutbox.length === 0 && attentionNotifications.length === 0) {
    return (
      <div className="conversations-panel conversations-panel--empty">
        <Mail size={24} strokeWidth={1.5} />
        <span>No conversations yet</span>
      </div>
    );
  }

  return (
    <div className="conversations-panel">
      {/* Pending Outbox Section - only for new emails without a thread */}
      {globalPendingOutbox.length > 0 && (
        <div className="conv-pending-section" ref={pendingSectionRef}>
          <div
            className={`conv-pending-header ${expandedPending ? 'expanded' : ''}`}
            onClick={() => setExpandedPending(!expandedPending)}
          >
            <div className="conv-pending-left">
              {expandedPending ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Timer size={16} className="bouncing-clock" />
              <span className="conv-pending-title">
                Pending
                <span className="conv-pending-count">{globalPendingOutbox.length}</span>
              </span>
            </div>
            <span className="conv-pending-hint">Will send when scheduler runs</span>
          </div>

          {expandedPending && (
            <div className="conv-pending-list">
              {globalPendingOutbox.map(item => {
                const isItemExpanded = expandedPendingItems[item.id];
                return (
                  <div key={item.id} className={`conv-pending-item ${isItemExpanded ? 'expanded' : ''}`}>
                    <div
                      className="conv-pending-header-row"
                      onClick={() => setExpandedPendingItems(prev => ({
                        ...prev,
                        [item.id]: !prev[item.id]
                      }))}
                    >
                      <div className="conv-pending-expand-icon">
                        {isItemExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                      <div className="conv-pending-info">
                        <span className="conv-pending-subject">
                          {item.subject || item.property?.address || 'New Email'}
                        </span>
                        <span className="conv-pending-meta">
                          To: {item.assignedEmails?.[0] || 'Unknown'}
                        </span>
                      </div>
                      <button
                        className="conv-cancel-btn"
                        onClick={(e) => handleCancelOutbox(item, e)}
                        disabled={cancellingIds.has(item.id)}
                      >
                        <XCircle size={14} />
                        Cancel
                      </button>
                    </div>
                    {isItemExpanded && (
                      <div className="conv-pending-body">
                        <div className="conv-pending-body-content">
                          <MessageBody
                            body={item.script || 'No email body available'}
                            className="conv-pending-message-body"
                          />
                          {(signatureMode === 'professional' || (signatureMode === 'custom' && emailSignature)) && (
                            <div className="conv-pending-signature">
                              <SignaturePreview
                                signatureMode={signatureMode}
                                emailSignature={emailSignature}
                                compact={true}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {attentionNotifications.length > 0 && (
        <section aria-label="Needs your attention" className="conv-attention-list">
          <h3>Needs your attention</h3>
          {campaignNotifications.map(notification => renderAttentionCard(notification))}
          {conversationNotifications.map(renderAttentionLine)}
        </section>
      )}

      {/* Sent Conversations */}
      {threads.map(thread => {
        const canStop = thread.status === 'active' || thread.status === 'paused';
        const isExpanded = expandedThreads[thread.id];

        // Check if thread has pending outbox items
        // Include thread.id (document ID) since outbox items use conversationId as threadId
        const allThreadIds = [thread.id, ...(thread.threadIds || [])];
        const pendingItems = allThreadIds.map(tid => threadPendingMap[tid] || []).flat();
        const hasPendingReply = pendingItems.length > 0;

        // Determine display status: pending items override normal status
        const displayStatus = hasPendingReply ? 'pending' : thread.status;
        const statusInfo = getStatusBadge(displayStatus);
        const StatusIcon = statusInfo.icon;

        // Check if awaiting response: active status + last message was outbound + no pending items
        const lastMessage = thread.messages?.[thread.messages.length - 1];
        const isAwaitingResponse = !hasPendingReply && thread.status === 'active' && lastMessage?.direction === 'outbound';

        // Find matching action notifications even if a prior action already resumed the thread.
        // Don't show composer if there's already a pending reply
        const matchingNotification = findNotificationForThread(thread, allClientNotifs, selectedActionNotification, threads);
        const terminalStatuses = new Set(['stopped', 'completed', 'closed']);
        const isDeadLetterReview = Boolean(matchingNotification?.meta?.deadLetterId);
        const actionThreadStatus = getNotificationThreadStatus(matchingNotification, thread);
        const isTerminalThread = terminalStatuses.has(actionThreadStatus);
        const isStoppedThread = actionThreadStatus === 'stopped';
        const replyAvailability = manualReplyAvailability(thread, { pendingReplyCount: pendingItems.length });
        const manualReplyNotification = !matchingNotification
          && manualReplyThreadId === thread.id
          && replyAvailability.enabled
          ? buildManualReplyNotification(thread, client)
          : null;
        const composerNotification = matchingNotification || manualReplyNotification;
        const showReplyComposer = isExpanded && !hasPendingReply && Boolean(composerNotification) && (
          matchingNotification
            ? (!isStoppedThread && (!isTerminalThread || isDeadLetterReview))
            : true
        );

        return (
          <div
            key={thread.id}
            className="conv-thread"
            ref={el => actionElementRefs.current[thread.id] = el}
          >
            <div
              className={`conv-thread-header ${isExpanded ? 'expanded' : ''}`}
              onClick={() => toggleThread(thread.id)}
            >
              <div className="conv-thread-left">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div className="conv-thread-info">
                  <div className="conv-thread-title-row">
                    <span className="conv-thread-subject">{thread.subject}</span>
                    {isAwaitingResponse ? (
                      <span className="conv-awaiting-badge">
                        <Timer size={10} className="bouncing-timer" />
                        Awaiting Response
                      </span>
                    ) : (
                      <span className={`conv-status-badge ${statusInfo.className}`}>
                        <StatusIcon size={10} />
                        {statusInfo.label}
                      </span>
                    )}
                  </div>
                  <span className="conv-thread-meta">
                    {thread.messages?.length || 0} message{thread.messages?.length !== 1 ? 's' : ''}
                    {thread.email?.[0] && <> &middot; {thread.email[0]}</>}
                  </span>
                </div>
              </div>
              <div className="conv-thread-right">
                <button
                  className="conv-reply-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!replyAvailability.enabled) return;
                    setManualReplyThreadId(thread.id);
                    setExpandedThreads(prev => ({ ...prev, [thread.id]: true }));
                  }}
                  disabled={!replyAvailability.enabled}
                  title={replyAvailability.enabled
                    ? `Reply to ${thread.subject || 'this conversation'}`
                    : replyAvailability.reason}
                  aria-label={`Reply to ${thread.subject || 'this conversation'}`}
                >
                  <Mail size={12} />
                  Reply
                </button>
                {canStop && (
                  <button
                    className="conv-stop-btn"
                    onClick={(e) => handleStopConversation(thread, e)}
                    disabled={stoppingIds.has(thread.id)}
                  >
                    {stoppingIds.has(thread.id) ? <Loader size={12} className="spinning" /> : <StopCircle size={12} />}
                    {stoppingIds.has(thread.id) ? 'Stopping...' : 'Stop'}
                  </button>
                )}
                <span className="conv-thread-time">
                  <Clock size={12} />
                  {formatDate(thread.updatedAt)}
                </span>
              </div>
            </div>

            {isExpanded && (
              <div className="conv-messages">
                {thread.messages?.map((message, index) => {
                  const nextMessage = thread.messages[index + 1];
                  const isLastMessage = index === thread.messages.length - 1;
                  const { reactions, newPropertyNotifs } = getNotificationsForMessage(
                    message, nextMessage, thread, isLastMessage
                  );

                  return (
                    <React.Fragment key={message.id}>
                      <div
                        className={`conv-message ${message.direction === 'outbound' ? 'outbound' : 'inbound'}`}
                      >
                        <div className="conv-message-header">
                          <div className="conv-message-direction">
                            {message.direction === 'outbound' ? (
                              <>
                                <ArrowRight size={12} />
                                <span>You</span>
                              </>
                            ) : (
                              <>
                                <ArrowLeft size={12} />
                                <span>{message.from || 'Broker'}</span>
                              </>
                            )}
                          </div>
                          <span className="conv-message-time">
                            {formatDate(message.sentDateTime || message.receivedDateTime)}
                          </span>
                        </div>
                        <MessageBody body={message.body} />
                        {/* Attachment links */}
                        {message.attachments?.length > 0 && (
                          <div className="conv-message-attachments">
                            {message.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.driveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="conv-attachment-link"
                              >
                                <FileText size={14} />
                                <span>{att.name || 'Attachment'}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Inline notifications triggered by this message */}
                      {reactions.length > 0 && (
                        <div className="conv-message-reactions">
                          {reactions.map((notif) => {
                            const display = getNotificationDisplay(notif);
                            const NotifIcon = display.icon;
                            return (
                              <div
                                key={notif.id}
                                className="conv-reaction"
                                style={{ backgroundColor: display.bgColor, borderColor: display.color }}
                              >
                                <NotifIcon size={12} style={{ color: display.color }} />
                                <span className="conv-reaction-label" style={{ color: display.color }}>
                                  {display.label}
                                </span>
                                {display.value && (
                                  <span className="conv-reaction-value">{display.value}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Inline New Property Card triggered by this message */}
                      {newPropertyNotifs.map(newPropertyNotif => (
                        <div
                          key={newPropertyNotif.id}
                          ref={el => actionInputRefs.current[newPropertyNotif.id] = el}
                        >
                          <InlineNewPropertyCard
                            notification={newPropertyNotif}
                            client={client}
                            onSend={handleNewPropertySent}
                            onDismiss={handleNewPropertySent}
                          />
                        </div>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* An opt-out notice joins these stopped-conversation cards.
                    renderAttentionCard is notice text plus Mark Handled and
                    nothing else, and the notice is still not sendable, so
                    isActionableNotification keeps it out of the composer. */}
                {allClientNotifs.filter(notification =>
                  getNotificationThreadStatus(notification, thread) === 'stopped' &&
                  notification.meta?.reason !== 'new_property_pending_approval' &&
                  (isActionableNotification(notification) || isOptOutNotice(notification)) &&
                  !isCampaignLevelActionNotification(notification) &&
                  notificationMatchesThread(notification, thread, threads)
                ).map(notification => renderAttentionCard(notification, thread))}

                {/* Inline Reply Composer for paused threads */}
                {showReplyComposer && (
                  <div ref={el => matchingNotification?.id && (actionInputRefs.current[matchingNotification.id] = el)}>
                    {matchingNotification && resolveError?.notificationId === matchingNotification?.id && resolveError?.message && (
                      <p className="conv-resolve-error" role="alert">{resolveError.message}</p>
                    )}
                    <InlineReplyComposer
                      thread={thread}
                      notification={composerNotification}
                      client={client}
                      onSend={() => {
                        setManualReplyThreadId(null);
                        handleThreadReplySent();
                      }}
                      onDismiss={() => {
                        if (manualReplyNotification) {
                          setManualReplyThreadId(null);
                          return;
                        }
                        // Just collapse without sending - user can reopen
                        toggleThread(thread.id);
                      }}
                      onResolve={matchingNotification
                        ? () => handleResolveActionNotification(thread, matchingNotification)
                        : undefined}
                      sendDisabledReason={
                        isDeadLetterReview && isTerminalThread
                          ? 'This conversation has ended. Mark handled to clear the saved draft; it cannot be sent from a closed conversation.'
                          : ''
                      }
                    />
                  </div>
                )}

                {/* Pending replies for this thread */}
                {[thread.id, ...(thread.threadIds || [])].map(tid => threadPendingMap[tid] || []).flat().map(item => (
                  <div key={item.id} className="conv-message outbound conv-message--pending">
                    <div className="conv-message-header">
                      <div className="conv-message-direction">
                        <ArrowRight size={12} />
                        <span>Pending Reply</span>
                        <Timer size={12} className="bouncing-timer" />
                      </div>
                      <button
                        className="conv-cancel-btn conv-cancel-btn--inline"
                        onClick={(e) => handleCancelOutbox(item, e)}
                        disabled={cancellingIds.has(item.id)}
                      >
                        <XCircle size={14} />
                        Cancel
                      </button>
                    </div>
                    <div className="conv-message-body">
                      <MessageBody
                        body={item.script || 'No email body available'}
                        className="conv-message-text"
                      />
                      {(signatureMode === 'professional' || (signatureMode === 'custom' && emailSignature)) && (
                        <div className="conv-pending-signature">
                          <SignaturePreview
                            signatureMode={signatureMode}
                            emailSignature={emailSignature}
                            compact={true}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

              </div>
            )}
          </div>
        );
      })}
      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.title}
        description={confirmAction?.description}
        confirmLabel={confirmAction?.confirmLabel}
        onCancel={() => setConfirmAction(null)}
        onConfirm={async () => {
          const action = confirmAction;
          setConfirmAction(null);
          await action?.onConfirm?.();
        }}
      />
    </div>
  );
}
